import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { CASE_STUDIES, CASE_GALLERIES } from "../data";
import Logo from "../components/vyft/Logo";
import { Reveal, Overline } from "../components/vyft/primitives";
import { Button } from "../components/ui/button";

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cs = CASE_STUDIES[slug];
  const gallery = CASE_GALLERIES[slug] || [];

  useEffect(() => {
    document.documentElement.classList.add("dark");
    window.scrollTo(0, 0);
  }, [slug]);

  if (!cs) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Case study not found</h1>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-brand-gradient text-white border-0">Back home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 px-4 pt-4">
        <div className="mx-auto max-w-7xl flex items-center justify-between rounded-full px-4 md:px-6 h-16 backdrop-blur-xl bg-background/70 border border-border/10">
          <button data-testid="cs-back" onClick={() => navigate("/")} className="flex items-center gap-2"><Logo /></button>
          <a href="/#contact" className="hidden sm:inline-flex"><Button className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">Start your project</Button></a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden grain">
        <div className="orb bg-[#FF1F8E] h-96 w-96 -top-32 -left-24" />
        <div className="orb bg-[#F97316] h-80 w-80 top-10 right-0" />
        <div className="relative z-10 mx-auto max-w-5xl">
          <button data-testid="cs-back-link" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to work
          </button>
          <div className="mt-8"><Overline>{cs.cat} · {cs.year}</Overline></div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]"
          >
            {cs.title}
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">{cs.summary}</p>
          <div className="mt-6 text-sm text-muted-foreground">Client — <span className="text-foreground font-medium">{cs.client}</span></div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-6">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden border border-border/10">
            <img src={cs.img} alt={cs.title} className="w-full h-[26rem] md:h-[34rem] object-cover" />
          </div>
        </Reveal>

        {/* Results */}
        <Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {cs.results.map((r) => (
              <div key={r.label} className="rounded-3xl border border-border/10 bg-card p-8 shimmer-card">
                <div className="font-display text-4xl md:text-5xl font-extrabold text-gradient">{r.metric}</div>
                <div className="mt-2 text-sm text-muted-foreground">{r.label}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Challenge + Approach */}
        <div className="mt-16 grid md:grid-cols-2 gap-12">
          <Reveal>
            <Overline>The Challenge</Overline>
            <p className="mt-5 text-muted-foreground leading-relaxed text-lg">{cs.challenge}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {cs.services.map((s) => (
                <span key={s} className="rounded-full border border-border/15 bg-card px-4 py-1.5 text-xs font-medium">{s}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Our Approach</Overline>
            <ul className="mt-5 space-y-4">
              {cs.approach.map((a) => (
                <li key={a} className="flex items-start gap-3">
                  <span className="mt-1 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-brand-gradient text-white"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-muted-foreground leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Gallery */}
        {gallery.length > 0 && (
          <Reveal>
            <div className="mt-16">
              <Overline>Inside the project</Overline>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {gallery.map((src, i) => (
                  <div
                    key={i}
                    data-testid={`cs-gallery-${i}`}
                    className={`group relative rounded-3xl overflow-hidden border border-border/10 ${i === 0 ? "sm:col-span-2" : ""}`}
                  >
                    <img
                      src={src}
                      alt={`${cs.title} ${i + 1}`}
                      className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i === 0 ? "h-80 md:h-[26rem]" : "h-64"}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}

        {/* Quote */}
        <Reveal>
          <blockquote className="mt-16 rounded-[2rem] border border-border/10 bg-card p-10 md:p-14 shimmer-card">
            <p className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-snug">“{cs.quote.text}”</p>
            <footer className="mt-6 text-sm text-muted-foreground">— {cs.quote.name}</footer>
          </blockquote>
        </Reveal>
      </div>

      {/* CTA */}
      <section className="mt-20 px-6 pb-24">
        <div className="mx-auto max-w-5xl rounded-[2rem] bg-brand-gradient p-10 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight">Ready to write your own success story?</h2>
          <p className="mt-4 text-white/85 max-w-xl mx-auto">Book a free consultation and let's build measurable growth together.</p>
          <a href="/#contact" data-testid="cs-cta">
            <Button className="mt-8 rounded-full h-13 px-8 py-6 bg-white text-primary hover:bg-white/90 text-base">
              Get Free Consultation <ArrowUpRight className="ml-1 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
